import { createAsyncThunk } from '@reduxjs/toolkit';
import { operationsSearch } from '@reducers/search';
import type { ProductionOperationSearchResponse } from '@api/domain/types';
import type { RootState, AppDispatch } from '@store/types';
import { PLANBOARD_OPERATION_SELECT } from '@components/search/components/select';
import { DEMO_MACHINE_ID, LEGACY_DEMO_MACHINE_ID } from '@components/tours/config/planboard/constants';

const isDemoMachineId = (id?: number) => id === DEMO_MACHINE_ID || id === LEGACY_DEMO_MACHINE_ID;

export const fetchMachineOperations = createAsyncThunk<
  { machineId?: number; response?: ProductionOperationSearchResponse; reset?: boolean } | null,
  { machineId?: number; skip?: number; top?: number; filter?: string; search?: string },
  { state: RootState; dispatch: AppDispatch }
>('planboard/fetchMachineOperations', async ({ machineId, skip, top, filter, search }, { dispatch }) => {
  // Skip fetching for demo machine IDs - their data is injected via setDemoOperations
  if (isDemoMachineId(machineId)) {
    return null;
  }

  const isReset = typeof skip !== 'number' || skip === 0;

  // No Machine column: operations not scheduled on any machine
  if (!machineId || machineId === -1) {
    const noMachineFilter = `Status ne 'Finished' and Status ne 'Running' and not Machines/any(machine: machine/LinkType eq 'ScheduledOn')${filter ? ` and (${filter})` : ''}`;

    const response = await dispatch(
      operationsSearch({
        data: { skip, top, search },
        filter: { oDataFilter: noMachineFilter.trim(), orderBy: ['ScheduledStartDate asc'] },
        select: PLANBOARD_OPERATION_SELECT,
        resetHistory: true,
        ignoreHistory: true,
      })
    );

    return { machineId, response, reset: isReset };
  }

  // Machine column: Running jobs at top, then scheduled jobs sorted by ExpectedStartOn
  const runningFilter = `Status eq 'Running' and Machines/any(machine: machine/LinkType eq 'ProducedOn' and machine/MachineId eq ${machineId})${filter ? ` and (${filter})` : ''}`;
  // Scheduled jobs filter (excluding Running and Finished)
  const scheduledFilter = `Status ne 'Finished' and Status ne 'Running' and Machines/any(machine: machine/LinkType eq 'ScheduledOn' and machine/MachineId eq ${machineId})${filter ? ` and (${filter})` : ''}`;

  // Fetch both Running and Scheduled operations in parallel
  const [runningResponse, scheduledResponse] = await Promise.all([
    dispatch(
      operationsSearch({
        data: { skip: 0, top: 100, search }, // Running jobs: fetch all (usually few)
        filter: { oDataFilter: runningFilter.trim() },
        select: PLANBOARD_OPERATION_SELECT,
        resetHistory: true,
        ignoreHistory: true,
      })
    ) as Promise<ProductionOperationSearchResponse>,
    dispatch(
      operationsSearch({
        data: { skip: isReset ? 0 : skip, top, search },
        filter: { oDataFilter: scheduledFilter.trim(), orderBy: ['ExpectedStartOn asc'] },
        select: PLANBOARD_OPERATION_SELECT,
        resetHistory: true,
        ignoreHistory: true,
      })
    ) as Promise<ProductionOperationSearchResponse>,
  ]);

  // Merge results: Running jobs first (pinned at top), then scheduled jobs sorted by ExpectedStartOn
  const runningItems = runningResponse?.searchResult ?? [];
  const scheduledItems = scheduledResponse?.searchResult ?? [];

  const mergedResponse: ProductionOperationSearchResponse = {
    searchResult: isReset ? [...runningItems, ...scheduledItems] : scheduledItems,
    totalResultCount: (runningResponse?.totalResultCount ?? 0) + (scheduledResponse?.totalResultCount ?? 0),
  };

  return { machineId, response: mergedResponse, reset: isReset };
});
